import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useTheme } from '../contexts/ThemeContext';

const VehicleChart = ({ data }) => {
  const { theme, getThemeClasses } = useTheme();
  const themeClasses = getThemeClasses();

  // Theme-aware colors
  const colors = {
    dark: {
      grid: '#4B5563',
      text: '#ffffff',
      tooltip: 'bg-gray-900 border-military-600',
      tooltipText: 'text-white',
      failures: '#ef4444',
      maintenance: '#10b981'
    },
    light: {
      grid: '#d1d5db',
      text: '#1f2937',
      tooltip: 'bg-white border-gray-300 shadow-lg',
      tooltipText: 'text-gray-900',
      failures: '#dc2626',
      maintenance: '#059669'
    }
  };

  const currentColors = colors[theme] || colors.dark;
  // Generate 5-Year Quarterly Failure Trend Data (Q2 and Q4 for each year)
  const currentYear = 2025;
  const quarterlyTrends = [];
  for (let i = 4; i >= 0; i--) {
    const year = currentYear - i;
    // Q2 data
    quarterlyTrends.push({
      quarter: `${year} Q2`,
      failures: Math.floor(Math.random() * 15) + 5, // Random failures between 5-20
      maintenanceEvents: Math.floor(Math.random() * 25) + 10, // Random maintenance 10-35
    });
    // Q4 data
    quarterlyTrends.push({
      quarter: `${year} Q4`,
      failures: Math.floor(Math.random() * 15) + 5,
      maintenanceEvents: Math.floor(Math.random() * 25) + 10,
    });
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className={`${currentColors.tooltip} border-2 rounded-lg p-3 shadow-xl`}>
          <p className={`${currentColors.tooltipText} font-bold text-sm mb-2`}>{label}</p>
          <div className="space-y-1">
            {payload.map((entry, index) => (
              <div key={index} className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded"
                  style={{ backgroundColor: entry.color }}
                />
                <span className={`${theme === 'light' ? 'text-gray-600' : 'text-gray-300'} text-xs capitalize`}>{entry.name}:</span>
                <span className={`${currentColors.tooltipText} font-semibold text-xs`}>{entry.value}</span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={`${themeClasses.chartBackground} border ${themeClasses.border} rounded-xl p-3 sm:p-4 lg:p-6 hover:border-military-600/50 transition-all`}>
      <h3 className={`text-base sm:text-lg lg:text-xl font-bold ${themeClasses.textPrimary} mb-4 sm:mb-6 lg:mb-8`}>5-Year Failure Trend Analysis</h3>
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart
          data={quarterlyTrends}
          margin={{ top: 5, right: 30, left: 0, bottom: 60 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={currentColors.grid} />
          <XAxis
            dataKey="quarter"
            stroke={currentColors.text}
            tick={{ fontSize: 11, fill: currentColors.text }}
            angle={-45}
            textAnchor="end"
            height={60}
          />
          <YAxis
            stroke={currentColors.text}
            tick={{ fontSize: 11, fill: currentColors.text }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{ fontSize: '12px', paddingTop: '10px', color: currentColors.text }}
            iconType="rect"
          />
          <Area
            type="monotone"
            dataKey="maintenanceEvents"
            name="Maintenance Events"
            stroke={currentColors.maintenance}
            fill={currentColors.maintenance}
            fillOpacity={theme === 'light' ? 0.3 : 0.6}
            strokeWidth={2}
          />
          <Area
            type="monotone"
            dataKey="failures"
            name="Failures"
            stroke={currentColors.failures}
            fill={currentColors.failures}
            fillOpacity={theme === 'light' ? 0.3 : 0.6}
            strokeWidth={2}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default VehicleChart;
